import React from 'react';
import { Gift } from 'lucide-react';
import { ReferralBox } from '../referral';
import FitCoinsBalance from '../fitcoins/FitCoinsBalance';
import { FitCoinBalance } from '../../services/fitcoin.service';

interface FittingModalProps {
  isOpen: boolean;
  onClose: () => void;
  physicsEnabled: boolean;
  setPhysicsEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  showTracking: boolean;
  setShowTracking: React.Dispatch<React.SetStateAction<boolean>>;
  environment: 'studio' | 'daylight' | 'evening' | 'runway';
  setEnvironment: React.Dispatch<React.SetStateAction<'studio' | 'daylight' | 'evening' | 'runway'>>;
  onShowReferralModal: () => void;
  onShowPurchaseModal: () => void;
  balanceData: FitCoinBalance | null;
  dailyFitCoinsUsed: number;
  dailyFitCoinsLimit: number;
}

const FittingModal: React.FC<FittingModalProps> = ({
  isOpen,
  physicsEnabled,
  setPhysicsEnabled,
  showTracking,
  setShowTracking,
  environment,
  setEnvironment,
  onShowReferralModal,
  onShowPurchaseModal,
  balanceData,
  dailyFitCoinsUsed,
  dailyFitCoinsLimit,
}) => {
  if (!isOpen) return null;

  return (
    <div className="space-y-6">
      {/* Share Referral Box */}
      <ReferralBox onClick={onShowReferralModal} />

      {/* Daily FitCoin Bonuses */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">FitCoins</h3>
          <FitCoinsBalance
            balanceData={balanceData}
            onPurchase={onShowPurchaseModal}
            compact
          />
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-amber-500 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${((dailyFitCoinsLimit - dailyFitCoinsUsed) / dailyFitCoinsLimit) * 100}%` }}
            />
          </div>
        </div>

        {/* Reset Info */}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <div className="w-2 h-2 bg-gray-400 rounded-full" />
          <span>Daily FitCoins reset at midnight UTC</span>
        </div>

        {/* Get Free FitCoins Button */}
        <button
          onClick={onShowReferralModal}
          className="w-full mt-4 py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 shadow-sm"
        >
          <Gift size={18} />
          Get free FitCoins
        </button>
      </div>

      {/* Motion Settings */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-4">Motion Settings</h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900">Physics Simulation</span>
            <button
              onClick={() => setPhysicsEnabled(!physicsEnabled)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                physicsEnabled ? 'bg-red-500' : 'bg-gray-300'
              }`}
            >
              <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                physicsEnabled ? 'translate-x-6' : 'translate-x-0'
              }`} />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900">Show Tracking Points</span>
            <button
              onClick={() => setShowTracking(!showTracking)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                showTracking ? 'bg-red-500' : 'bg-gray-300'
              }`}
            >
              <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                showTracking ? 'translate-x-6' : 'translate-x-0'
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* Environment */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-4">Environment</h3>

        <div className="grid grid-cols-2 gap-2">
          {(['studio', 'daylight', 'evening', 'runway'] as const).map((env) => (
            <button
              key={env}
              onClick={() => setEnvironment(env)}
              className={`py-3 px-4 rounded-xl font-medium text-sm transition-all ${
                environment === env
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {env.charAt(0).toUpperCase() + env.slice(1)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FittingModal;
